import { useCallback, useEffect, useState } from 'react';
import { crmApi } from '@/api/crm.api';
import { leadgenApi, type LeadGenRoutingRule } from '@/api/leadgen.api';
import type { CrmTerritory } from '@/types/crm';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmployeeSelect } from '@/components/selects';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';
import { formatTableCellValue } from '@/utils/tableCellValue';

export function LeadGenRoutingPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const { getLabel: getEmployeeLabel } = useEmployeeOptions();
  const [rules, setRules] = useState<LeadGenRoutingRule[]>([]);
  const [territories, setTerritories] = useState<CrmTerritory[]>([]);
  const [ruleName, setRuleName] = useState('');
  const [priorityOrder, setPriorityOrder] = useState('1');
  const [conditionJson, setConditionJson] = useState('{"country": "IN"}');
  const [assignEmployeeId, setAssignEmployeeId] = useState<string>('');
  const [assignTeamCode, setAssignTeamCode] = useState('');
  const [territoryId, setTerritoryId] = useState('');

  const load = useCallback(async () => {
    try {
      setRules(await leadgenApi.listRoutingRules());
      setTerritories(await crmApi.listTerritories().catch(() => []));
    } catch (err) {
      showApiError(err, 'Failed to load routing requirements');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!ruleName.trim()) return;
    try {
      await leadgenApi.createRoutingRule({
        ruleName,
        priorityOrder: Number(priorityOrder),
        conditionJson,
        assignEmployeeId: assignEmployeeId ? Number(assignEmployeeId) : undefined,
        assignTeamCode: assignTeamCode || undefined,
        territoryId: territoryId ? Number(territoryId) : undefined,
        active: true
      });
      notify.success('Routing rule saved');
      setRuleName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create routing rule');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Lead Routing & Territories"
        description="Configure automated assignment criteria by country, region, state, or product, with integrated round-robin distribution."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Lead Routing' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
        <div className="lg:col-span-2 card p-5 space-y-4">
          <h3 className="section-title">Define Routing Criteria</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Rule Name" placeholder="e.g. India Region Routing" value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
            <Input label="Priority Order" type="number" placeholder="e.g. 1" value={priorityOrder} onChange={(e) => setPriorityOrder(e.target.value)} />
            <Input label="Match Criteria (JSON)" placeholder='e.g. {"country": "IN", "industry": "retail"}' value={conditionJson} onChange={(e) => setConditionJson(e.target.value)} />
            <EmployeeSelect
              label="Assign to employee"
              value={assignEmployeeId}
              onChange={setAssignEmployeeId}
              placeholder="Leave blank for round-robin queue"
            />
            <Input label="Team / Queue Code (Optional)" placeholder="e.g. IN_SALES_TEAM" value={assignTeamCode} onChange={(e) => setAssignTeamCode(e.target.value)} />
            <div>
              <Select
                label="CRM territory (optional)"
                value={territoryId}
                onChange={(v) => setTerritoryId(v)}
                placeholder="— None —"
                options={territories.map((t) => ({
                  value: String(t.id),
                  label: t.name,
                }))}
              />
              {territories.length === 0 && (
                <p className="text-[10px] text-slate-400 mt-1">
                  Define territories in CRM setup first.
                </p>
              )}
            </div>
          </div>
          <Button className="w-full" onClick={create}>Save Routing Policy</Button>
        </div>
        )}

        <div className={`card p-5 space-y-4 ${canManage ? '' : 'lg:col-span-3'}`}>
          <h3 className="section-title">Active Routing Policies</h3>
          <ul className="space-y-4">
            {rules.map(r => (
              <li key={r.id} className="border border-slate-100 rounded-lg p-4 bg-slate-50 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800">{r.ruleName}</p>
                    <p className="text-xs text-slate-400">Priority: {r.priorityOrder}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                    {r.assignEmployeeId ? getEmployeeLabel(r.assignEmployeeId, true) : 'Round Robin Queue'}
                  </span>
                </div>
                {r.territoryId != null && (
                  <p className="text-xs text-slate-500">
                    Territory: {territories.find((t) => t.id === r.territoryId)?.name ?? `#${r.territoryId}`}
                  </p>
                )}
                <p className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {formatTableCellValue(r.conditionJson)}
                </p>
              </li>
            ))}
            {rules.length === 0 && (
              <li className="text-center p-4 text-slate-400 text-sm">No routing rules created. Leads will fallback to circular round-robin list.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
